/**
 * TradeLife CORE - Theme System Configuration
 * 
 * Available Themes:
 * - Commercial Base (ENABLED) - Default professional blue theme
 * - Molten Copper (ENABLED) - Premium warm copper theme
 * - Electric (ENABLED) - Tech/modern purple theme
 * - Growth (ENABLED) - Fresh green growth theme
 * - Lest We Forget (ENABLED) - Remembrance poppy red theme
 */

export const themes = {
  commercial: {
    id: 'commercial',
    name: 'Commercial Base',
    enabled: true,
    description: 'Professional, council-safe design',
    seasonal: false,
    loginEmblem: '/emblems/uk.svg',
    preview: {
      primary: '#3b82f6',
      secondary: '#1e40af',
      accent: '#60a5fa'
    },
    colors: {
      accent: '#3b82f6',
      accentHover: '#2563eb',
      accentMuted: 'rgba(59, 130, 246, 0.15)',
      accentGlow: 'rgba(59, 130, 246, 0.4)',
    },
    features: {
      embers: false,
      glowEffects: true,
      glassmorphism: true,
    }
  },
  moltenCopper: {
    id: 'moltenCopper',
    name: 'Molten Copper',
    enabled: true,
    description: 'Warm, premium copper accents',
    seasonal: false,
    loginEmblem: '/emblems/copper.svg',
    preview: {
      primary: '#c77d42',
      secondary: '#8b5a2b',
      accent: '#d4a574'
    },
    colors: {
      accent: '#c77d42',
      accentHover: '#a86835',
      accentMuted: 'rgba(199, 125, 66, 0.15)',
      accentGlow: 'rgba(199, 125, 66, 0.4)',
    },
    features: {
      embers: true,
      glowEffects: true,
      glassmorphism: true,
    }
  },
  electric: {
    id: 'electric',
    name: 'Electric',
    enabled: true,
    description: 'Modern, high-tech purple',
    seasonal: false,
    loginEmblem: '/emblems/electric.svg',
    preview: {
      primary: '#8b5cf6',
      secondary: '#6d28d9',
      accent: '#a78bfa'
    },
    colors: {
      accent: '#8b5cf6',
      accentHover: '#7c3aed',
      accentMuted: 'rgba(139, 92, 246, 0.15)',
      accentGlow: 'rgba(139, 92, 246, 0.4)',
    },
    features: {
      embers: false,
      glowEffects: true,
      glassmorphism: true,
    }
  },
  tradeSpecific: {
    id: 'tradeSpecific',
    name: 'Growth',
    enabled: true,
    description: 'Fresh, growth-focused green',
    seasonal: false,
    loginEmblem: '/emblems/growth.svg',
    preview: {
      primary: '#10b981',
      secondary: '#047857',
      accent: '#34d399'
    },
    colors: {
      accent: '#10b981',
      accentHover: '#059669',
      accentMuted: 'rgba(16, 185, 129, 0.15)',
      accentGlow: 'rgba(16, 185, 129, 0.4)',
    },
    features: {
      embers: false,
      glowEffects: true,
      glassmorphism: true,
    }
  },
  lestWeForget: {
    id: 'lestWeForget',
    name: 'Lest We Forget',
    enabled: true,
    description: 'Remembrance — solemn poppy red',
    seasonal: false,
    loginEmblem: '/emblems/poppy.svg',
    preview: {
      primary: '#b91c1c',
      secondary: '#7f1d1d',
      accent: '#dc2626'
    },
    colors: {
      accent: '#b91c1c',
      accentHover: '#991b1b',
      accentMuted: 'rgba(185, 28, 28, 0.15)',
      accentGlow: 'rgba(185, 28, 28, 0.35)',
    },
    features: {
      embers: false,
      glowEffects: true,
      glassmorphism: true,
    }
  }
};

// Get enabled themes as array
export const getEnabledThemes = () => {
  return Object.values(themes).filter(t => t.enabled);
};

// Get theme by ID
export const getThemeById = (themeId) => {
  return themes[themeId] || themes.commercial;
};

// Get active theme from localStorage or default
export const getActiveTheme = () => {
  const savedThemeId = localStorage.getItem('tradelife_theme');
  if (savedThemeId && themes[savedThemeId]?.enabled) {
    return themes[savedThemeId];
  }
  return themes.commercial;
};

// Apply theme to document
export const applyTheme = (themeId) => {
  const theme = themes[themeId] || themes.commercial;
  
  // Apply CSS variables to root
  document.documentElement.style.setProperty('--accent-primary', theme.colors.accent);
  document.documentElement.style.setProperty('--accent-primary-hover', theme.colors.accentHover);
  document.documentElement.style.setProperty('--accent-muted', theme.colors.accentMuted);
  document.documentElement.style.setProperty('--accent-glow', theme.colors.accentGlow);
  
  // Store in localStorage
  localStorage.setItem('tradelife_theme', theme.id);
  
  return theme;
};

// Initialize theme on app load
export const initializeTheme = () => {
  const savedThemeId = localStorage.getItem('tradelife_theme');
  if (savedThemeId && themes[savedThemeId]?.enabled) {
    applyTheme(savedThemeId);
    return themes[savedThemeId];
  }
  // Apply default
  applyTheme('commercial');
  return themes.commercial;
};

export const isThemeEnabled = (themeId) => {
  return themes[themeId]?.enabled || false;
};

export default themes;
